<template>
  <q-input
    v-model="formattedDate"
    readonly
    outlined
    color="input"
    :label="label || t('date')"
    :mask="dateInputMask"
    :rules="rules"
    :error="error"
    :error-message="errorMessage"
    no-error-icon
    hide-hint
    class="datepicker-label"
    input-class="cursor-pointer"
    :class="{ 'q-field--highlighted': openDatePicker }"
    :data-testid="`${identifier}-input`"
    @click="qDateProxy.show()"
  >
    <template #append>
      <q-popup-proxy
        ref="qDateProxy"
        transition-show="scale"
        transition-hide="scale"
        class="box-shadow"
        :data-testid="identifier"
        @before-show="openDatePicker = true"
        @before-hide="openDatePicker = false"
      >
        <q-date
          ref="refDate"
          v-model="selectedDate"
          minimal
          bordered
          square
          :range="false"
          :no-unset="noUnset"
          color="grey-1"
          first-day-of-week="1"
          :options="options"
          :locale="locale"
          :default-view="dateDefaultView"
          :default-year-month="defaultYearMonth"
          :years-in-month-view="!onlyMonthsWithoutYear"
          :emit-immediately="onlyYears || onlyMonths || onlyMonthsWithoutYear"
          :navigation-max-year-month="navigationMaxYearMonth ? format(navigationMaxYearMonth, 'yyyy/MM') : undefined"
          @mousewheel.prevent="onWheel"
          @mousedown="unsetWheel"
          @update:model-value="dateSelected"
        >
        </q-date>
      </q-popup-proxy>
      <q-icon
        v-if="!clearable || (clearable && !formattedDate)"
        name="calendar_today"
        class="cursor-pointer"
        size="16px"
      ></q-icon>
      <q-icon
        v-if="clearable && formattedDate"
        data-testid="datepicker-clear-btn"
        name="clear"
        size="18px"
        class="cursor-pointer z-max"
        @click.stop.prevent="clearSelection()"
      />
    </template>
  </q-input>
</template>

<script lang="ts" setup>
import { toRefs, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { QPopupProxy, QDate, ValidationRule } from 'quasar';
import { format } from 'date-fns';

const OFFSET_CALENDAR_TYPE = 'month';

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = withDefaults(
  defineProps<{
    identifier?: string;
    date?: string;
    onlyMonths?: boolean;
    onlyMonthsWithoutYear?: boolean;
    onlyYears?: boolean;
    label?: string;
    options?: (date: string) => boolean;
    rules?: ValidationRule[];
    error?: boolean;
    errorMessage?: string;
    value?: [] | string;
    noUnset?: boolean;
    clearable?: boolean;
    navigationMaxYearMonth?: Date;
    defaultYearMonth?: string;
  }>(),
  {
    date: undefined,
    error: undefined,
    errorMessage: undefined,
    identifier: undefined,
    onlyMonths: undefined,
    onlyMonthsWithoutYear: undefined,
    onlyYears: undefined,
    rules: (): ValidationRule[] => [],
    options: undefined,
    label: undefined,
    value: '',
    noUnset: false,
    clearable: false,
    navigationMaxYearMonth: undefined,
    defaultYearMonth: undefined,
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['update:date']);
const UPDATE_DATE_EVENT = 'update:date';

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const { t, tm } = useI18n();
const { date, onlyYears, onlyMonths, onlyMonthsWithoutYear } = toRefs(props);
const dateFormat = ref(t('dateFormat'));
const selectedDate = ref(date.value && date.value !== '' ? format(new Date(date.value), 'yyyy/MM/dd') : '');
const qDateProxy = ref(QPopupProxy);
const locale = ref({
  days: tm('weekDay') as [],
  daysShort: tm('weekDayShort') as [],
  months: tm('months') as [],
  monthsShort: tm('monthsShort') as [],
});
const dateInputMask = ref(t('dateInputFormat'));
const dateDefaultView = ref<'Calendar' | 'Months' | 'Years' | undefined>('Calendar');
const formattedDate = ref('');
const openDatePicker = ref(false);
const calendarDate = ref('');
const mouseWheel = ref(false);

const reasons = {
  ADD_DAY: 'add-day',
  REMOVE_DAY: 'remove-day',
  MONTH: 'month',
  YEAR: 'year',
};

if (onlyMonths.value) {
  dateInputMask.value = '####-##';
  dateDefaultView.value = 'Months';
  dateFormat.value = t('dateMonthFormat');

  if (selectedDate.value !== '') {
    selectedDate.value = format(new Date(selectedDate.value), 'yyyy/MM/dd');
  }
}

if (onlyMonthsWithoutYear.value) {
  dateInputMask.value = '';
  dateDefaultView.value = 'Months';
  dateFormat.value = t('dateMonthWithoutYearFormat');

  if (selectedDate.value !== '') {
    selectedDate.value = format(new Date(selectedDate.value), 'yyyy/MM/dd');
  }
}

if (onlyYears.value) {
  dateInputMask.value = '####';
  dateDefaultView.value = 'Years';
  dateFormat.value = t('dateYearFormat');
}

const refDate = ref(QDate);
const setFormattedDate = () => {
  calendarDate.value = selectedDate.value;
  formattedDate.value =
    selectedDate.value && selectedDate.value !== 'Invalid Date'
      ? format(new Date(selectedDate.value), dateFormat.value)
      : '';
};

setFormattedDate();
watch(selectedDate, () => {
  setFormattedDate();
});

const unsetWheel = () => {
  mouseWheel.value = false;
};
const onWheel = (e: WheelEvent) => {
  const decrement = e.deltaY > 0;
  refDate.value.offsetCalendar(OFFSET_CALENDAR_TYPE, decrement);
  mouseWheel.value = true;
};

const clearSelection = () => {
  formattedDate.value = '';
  selectedDate.value = '';
  emit(UPDATE_DATE_EVENT, formattedDate.value);
};

const dateSelected = (value: string, reason: string) => {
  // skip if only months calendar is open and mousewheeel is used
  if (onlyYears.value && reason === reasons.YEAR && mouseWheel.value) {
    return;
  }
  // skip if only months calendar is open and mousewheeel is used
  if ((onlyMonths.value || onlyMonthsWithoutYear.value) && reason === reasons.MONTH && mouseWheel.value) {
    return;
  }
  if ((onlyMonths.value || onlyMonthsWithoutYear.value) && reason === reasons.YEAR) {
    refDate.value.setView('Months');
  }
  if (reason === (reasons.ADD_DAY || reasons.REMOVE_DAY)) {
    qDateProxy.value.hide();
    emit(UPDATE_DATE_EVENT, value);
    return;
  }
  if (onlyYears.value && reason === reasons.YEAR) {
    qDateProxy.value.hide();
    emit(UPDATE_DATE_EVENT, format(new Date(value), 'yyyy'));
  }
  if (onlyMonths.value && reason === reasons.MONTH) {
    qDateProxy.value.hide();
    emit(UPDATE_DATE_EVENT, format(new Date(value), 'yyyy-MM'));
  }
  if (onlyMonthsWithoutYear.value && reason === reasons.MONTH) {
    qDateProxy.value.hide();
    emit(UPDATE_DATE_EVENT, format(new Date(value), 'MM'));
  }
  openDatePicker.value = false;
};
</script>
